import { AnyAction, Store } from 'redux';
import State from 'store/State';
import {
    selectIsDirty,
    selectLayout,
    selectLayoutComponents,
    selectLayoutManualSave,
} from 'layout/store/selectors';
import { ThunkDispatch } from 'store/store';
import { layoutSavedAction, setLayoutDirty } from 'layout/store/actions';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import { selectWidgetComponentConfigs } from 'widgets/store/selectors';
import { selectWidgetsSharedConfigState } from 'widgets/store/sharedConfiguration/selectors';
import { mapToUIConfigFromState } from 'layout/layoutRequestMapper';
import { buildErrorMessage } from 'requestProgress/RequestProgressService';
import { showWarningAsync } from 'notification/store/thunks';
import { WidgetsService } from 'widgets/WidgetsService';
import LayoutService from 'layout/LayoutService';
import { LoggingService } from '@instinet-analytics/core-lib';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';

const LAYOUT_SAVE_TIMEOUT = 5000;

export interface CurrentLayoutPersistenceServiceOptions {
    widgetsService: WidgetsService;
    layoutService: LayoutService;
    loggingService: LoggingService;
}

/*
 * Service for handling saving of the users current layout
 */
export class CurrentLayoutPersistenceService {
    private readonly widgetsService: WidgetsService;
    private readonly layoutService: LayoutService;
    private readonly logger: Logger;

    public store: Store<State, AnyAction> | undefined;

    public constructor(options: CurrentLayoutPersistenceServiceOptions) {
        this.layoutService = options.layoutService;
        this.widgetsService = options.widgetsService;
        this.logger = buildLogger(
            'CurrentLayoutPersistenceService',
            options.loggingService
        );
    }

    private layoutSaveTimeoutId: NodeJS.Timeout | undefined;

    /*
     * Persists the current layout with a debounce of 5 seconds - unless manual save is set, in which case it just sets it dirty
     */
    public async debouncePersist() {
        const manualSave = selectLayoutManualSave(this.getState());
        if (manualSave) {
            this.dispatch(setLayoutDirty(true));
            return;
        }

        this.clearTimeout();
        this.layoutSaveTimeoutId = setTimeout(() => {
            this.persistIfDirty();
        }, LAYOUT_SAVE_TIMEOUT);
    }

    /*
     * If there is a pending save (i.e. an outstanding debounced persist), then does it immediately
     */
    public async persistIfDirty() {
        const isDirtyManualSave = selectIsDirty(this.getState());

        const needToSave = isDirtyManualSave || this.layoutSaveTimeoutId;
        if (!needToSave) {
            return;
        }

        this.clearTimeout();

        return await this.persist();
    }

    private async persist() {
        if (!this.store) {
            throw new Error('store must be set');
        }

        this.logger.debug('Persisting');

        const state = this.store.getState();
        const layout = selectLayout(state)!;

        try {
            const appSettings = selectAppSettings(state);
            const components = selectLayoutComponents(state);
            const componentConfigurations = selectWidgetComponentConfigs(
                state,
                components,
                this.widgetsService
            );
            const sharedConfigs = selectWidgetsSharedConfigState(state);
            const uiConfig = mapToUIConfigFromState(
                layout,
                appSettings,
                componentConfigurations,
                sharedConfigs
            );

            const result = await this.layoutService.saveLayout(uiConfig);
            this.dispatch(
                layoutSavedAction(result.name, result.id!, result.origin)
            );
        } catch (error) {
            this.logger.warn('Error persisting layout', error);
            const message = buildErrorMessage(error, 'Error saving layout');
            this.dispatch(showWarningAsync(message));
        }
    }

    private clearTimeout() {
        if (this.layoutSaveTimeoutId) {
            clearTimeout(this.layoutSaveTimeoutId);
            this.layoutSaveTimeoutId = undefined;
        }
    }

    private get dispatch(): ThunkDispatch<AnyAction> {
        this.assertStore();
        return this.store!.dispatch;
    }

    private getState(): State {
        this.assertStore();
        return this.store!.getState();
    }

    private assertStore() {
        if (!this.store) {
            throw new Error('Store must be set');
        }
    }
}
